import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { hot } from 'react-hot-loader';
import { Route, Switch, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Spinner } from '@blueprintjs/core';
import { autobind } from 'core-decorators';

import { logout, getCurrentUser } from 'actions/user';
import { login as authkitLogin, getLogoutUrl, isCallbackPath } from 'auth/authkit';

import Login from './Login';
import AuthCallback from './AuthCallback';
import Nav from './Nav';
import Dashboard from './Dashboardv2';
import Organizations from './Organizations';
import Users from './Users';
import Devices from './Devices';
import Nvrs from './Nvrs';
import Sites from './Sites';
import Zones from './Zones';
import Locations from './Locations';
import Pipelines from './Pipelines';
import Audits from './Audits';
import ModelTraining from './ModelTraining';
import Permissions from './Permissions';
import PermissionedRoute from './Permissions/Route';
import PermissionedRedirect from './Permissions/Redirect';
import Alerts from './Alerts';
import Reid from './Reid';

const redirect = () => <Redirect to="/audits" />;

class App extends React.Component {
  componentDidMount() {
    const { loggedIn, dispatch, user } = this.props;
    // If the stored access token is already stale, skip the doomed fetch:
    // initAuth refreshes it immediately and componentDidUpdate fetches after.
    const stale = user.token && user.token.refresh_token
      && user.token.expires && user.token.expires <= new Date().getTime();
    if (loggedIn && !stale) {
      dispatch(getCurrentUser());
    }
  }

  componentDidUpdate(prevProps) {
    const { loggedIn, dispatch, user } = this.props;
    if (!loggedIn) {
      return;
    }
    const justLoggedIn = !prevProps.loggedIn;
    const tokenRefreshed = user.token !== prevProps.user.token;
    if (justLoggedIn || (tokenRefreshed && !user.profile)) {
      dispatch(getCurrentUser());
    }
  }

  @autobind
  onLogin() {
    // Return to the page the user originally requested after AuthKit login.
    return authkitLogin(`${window.location.pathname}${window.location.search}`);
  }

  @autobind
  onLogout() {
    const { dispatch, user } = this.props;
    const logoutUrl = getLogoutUrl(user.token);
    dispatch(logout());
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  @autobind
  renderLogin() {
    const { r } = this.props;
    if (r && isCallbackPath(r.pathname)) {
      return <AuthCallback />;
    }
    return <Login onLogin={this.onLogin} />;
  }

  renderApp() {
    const { user } = this.props;
    if (!(user.profile || {}).id) {
      return (
        <div style={{ paddingTop: 50 }}>
          <Spinner size={100} />
        </div>
      );
    }
    return (
      <div style={{ paddingTop: 16 }}>
        <Switch>
          <PermissionedRoute
            path="/organizations"
            component={Organizations}
            permissions={Permissions.Organizations}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/users"
            component={Users}
            permissions={Permissions.Users}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/devices"
            component={Devices}
            permissions={Permissions.Devices}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/nvrs"
            component={Nvrs}
            permissions={Permissions.Nvrs}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/sites"
            component={Sites}
            permissions={Permissions.Sites}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/zones"
            component={Zones}
            permissions={Permissions.Zones}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/locations"
            component={Locations}
            permissions={Permissions.Locations}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/pipelines"
            component={Pipelines}
            permissions={Permissions.Pipelines}
            renderNoPermissions={redirect}
          />
          <Route
            path="/audits"
            component={Audits}
            // permissions={Permissions.Audits}
            // renderNoPermissions={redirect}
          />
          <Route
            path="/reid"
            component={Reid}
          />
          <PermissionedRoute
            path="/dashboard"
            component={Dashboard}
            permissions={Permissions.Dashboard}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/model_training"
            component={ModelTraining}
            permissions={Permissions.ModelTraining}
            renderNoPermissions={redirect}
          />
          <PermissionedRoute
            path="/alerts"
            component={Alerts}
            permissions={Permissions.Alerts}
            renderNoPermissions={redirect}
          />
          <PermissionedRedirect
            to="/dashboard"
            permissions={Permissions.Dashboard}
            renderNoPermissions={redirect}
          />
        </Switch>
      </div>
    );
  }

  render() {
    const { loggedIn, userName } = this.props;
    const children = (loggedIn) ? this.renderApp() : this.renderLogin();

    return (
      <div className="bp3-dark app">
        { loggedIn && <Nav onLogout={this.onLogout} name={userName} /> }
        {children}
      </div>
    );
  }
}

App.propTypes = {
  loggedIn: PropTypes.bool,
  userName: PropTypes.string,
  dispatch: PropTypes.func,
  user: PropTypes.object,
  r: PropTypes.object,
};

export default compose(
  hot(module),
  connect(state => ({
    loggedIn: !!state.currentUser.token && !!state.currentUser.token.access_token,
    userName: state.currentUser.profile ? (state.currentUser.profile.name || state.currentUser.profile.email) : '',
    r: state.router.location,
    user: state.currentUser,
  })),
)(App);
